import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@fontsource/instrument-sans/latin-400.css';
import '@fontsource/instrument-sans/latin-500.css';
import '@fontsource/instrument-sans/latin-600.css';
import '@fontsource/instrument-sans/latin-700.css';
import '@fontsource/instrument-sans/latin-400-italic.css';
import '@fontsource/instrument-serif/latin-400.css';
import '@fontsource/instrument-serif/latin-400-italic.css';
import './index.css';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import AnalyticsListener from './utils/AnalyticsListener';
import reportWebVitals from './reportWebVitals';

const App = lazy(() => import('./App'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const FAQ = lazy(() => import('./pages/FAQ'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const LearnAI = lazy(() => import('./pages/LearnAI'));
const Intwise = lazy(() => import('./pages/Intwise'));
const AppsPage = lazy(() => import('./pages/AppsPage'));
const AppDetail = lazy(() => import('./pages/AppDetail'));
const ColorTest = lazy(() => import('./pages/ColorTest'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '100vh',
    fontSize: '1rem',
    color: '#6a6760',
    fontFamily: 'Instrument Sans, Segoe UI, system-ui, sans-serif'
  }}>
    Loading...
  </div>
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsListener />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<App />} />
              <Route path="/intwise" element={<Intwise />} />
              <Route path="/apps" element={<AppsPage />} />
              <Route path="/apps/:appId" element={<AppDetail />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacypolicy" element={<Navigate to="/privacy/" replace />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/cookiepolicy" element={<Navigate to="/cookies/" replace />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/learn-ai" element={<LearnAI />} />
              <Route path="/learnai" element={<Navigate to="/learn-ai/" replace />} />
              <Route path="/color-test" element={<ColorTest />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

reportWebVitals();
